import { IDestroyOptions, Point, Sprite } from "pixi.js";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { BasePageView } from "../../../../../common/components/BasePageView";
import { SoundsResourcePackageWrapper } from "../../../../../common/resources/SoundsResourcePackageWrapper";
import { PromiseCompletionSource } from "../../../../../../../shared/async/PromiseCompletionSource";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { RewardContainer } from "../../../components/giftsummary/RewardContainer";
import { FadeScreen } from "../../../../../game/components/FadeScreen";
import { RollProgressCounter } from "../../../../../common/components/RollProgressCounter";
import { ZoomBlurFilter } from "pixi-filters";
import { IPlayerModel } from "../../../../../common/models/IPlayerModel";
import { GiftResourcePackage } from "../../../resources/GiftResourcePackage";
import { DiceCollectAnimationView } from "../../../components/giftsummary/DiceCollectAnimationView";
import { gsap, Bounce, Power2 } from "gsap";
import { IGiftBoxPopupController } from "../../../controller/IGiftBoxPopupController";
import { GiftDiceRollsAction } from "../../../../../game/flow/impl/diceActions/GiftDiceRollsAction";

export class GiftDiceRollsPage extends BasePageView {
    private static readonly LOGO_SCALE = 1;
    private static readonly REWARD_CONTAINER_SCALE = 1.15;

    private readonly commonResources: CommonResourcesPackage;
    private readonly giftResources: GiftResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly viewCompletionSource: PromiseCompletionSource<void>;
    private readonly playerModel: IPlayerModel;
    private readonly giftBoxPopupController: IGiftBoxPopupController;
    private readonly diceRollAction: GiftDiceRollsAction;
    private glow: Sprite;
    private background: Sprite;
    private wonLogo: Sprite;
    private rewardContainer: RewardContainer;
    private collectAnimation: DiceCollectAnimationView;
    private collectAnimationPoint: Point;
    private hideViewTarget: Point;
    private fadeScreen: FadeScreen;
    private rollProgress: RollProgressCounter;
    private currentAnimation: gsap.core.Timeline;

    public constructor(
        commonResources: CommonResourcesPackage,
        giftResources: GiftResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        playerModel: IPlayerModel,
        giftBoxPopupController: IGiftBoxPopupController,
        diceRollAction: GiftDiceRollsAction,
        viewCompletionSource: PromiseCompletionSource<void>,
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.giftResources = giftResources;
        this.soundResources = soundResources;
        this.playerModel = playerModel;
        this.giftBoxPopupController = giftBoxPopupController;
        this.diceRollAction = diceRollAction;
        this.viewCompletionSource = viewCompletionSource;
        this.build();
        this.soundResources.assets_sounds_Collect_Screen_mp3.play();
        this.once("added", this.show.bind(this));
        this.eventMode = "static";
        this.on("pointerup", this.skipAnimations, this);
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.0, 0.0, 0.9, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.glow = new Sprite(this.giftResources.assets_views_gift_ui_gift_dice_rolls_glow_png);
        this.glow.anchor.set(0.5, 0.5);
        this.addChild(this.glow);
        this.background = new Sprite(this.giftResources.assets_views_gift_ui_gift_dice_rolls_background_png);
        this.background.anchor.set(0.5, 0.5);
        this.addChild(this.background);
        const blurFilter = new ZoomBlurFilter({
            center: new Point(this.canvasScaler.referenceWidth * 0.25, this.canvasScaler.referenceHeight * 0.25),
            radius: this.height,
            strength: 0.1,
            innerRadius: this.width * 0.1
        });
        this.background.filters = [blurFilter];
        this.wonLogo = new Sprite(this.giftResources.assets_views_gift_ui_gift_dice_rolls_won_png);
        this.wonLogo.scale.set(GiftDiceRollsPage.LOGO_SCALE);
        this.wonLogo.anchor.set(0.5, 0.5);
        this.addChild(this.wonLogo);
        this.rewardContainer = new RewardContainer(this.giftResources.assets_views_gift_ui_gift_dice_rolls_dice_icon_png, this.diceRollAction.diceRollGiftRewardInDiceRolls);
        this.rewardContainer.scale.set(GiftDiceRollsPage.REWARD_CONTAINER_SCALE);
        this.addChild(this.rewardContainer);
        this.rollProgress = new RollProgressCounter(
            this.commonResources.assets_common_ui_progress_counter_bottom_progress_png,
            this.commonResources.assets_common_ui_progress_counter_yellow_progress_png,
            this.playerModel.rolls.value,
            this.playerModel.rolls.value + this.diceRollAction.diceRollGiftRewardInDiceRolls
        );
        this.addChild(this.rollProgress);
        this.collectAnimationPoint = new Point();
        this.collectAnimation = new DiceCollectAnimationView(
            [this.giftResources.assets_views_gift_ui_gift_dice_rolls_dice_icon_png],
            this.giftResources.assets_views_gift_ui_gift_dice_rolls_glow_png,
            this.diceRollAction.diceRollGiftRewardInDiceRolls,
            this.collectAnimationPoint
        );
        this.collectAnimation.diceCollected.add(this.rollProgress.incrementProgress, this.rollProgress);
        this.addChild(this.collectAnimation);
        super.build();
    }

    private async show(): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.pause(0);
        animationTimeLine.fromTo(this.wonLogo.scale, { x: 0, y: 0 }, { x: GiftDiceRollsPage.LOGO_SCALE, y: GiftDiceRollsPage.LOGO_SCALE, duration: 0.3, ease: "back.out(2)" });
        animationTimeLine.fromTo(this.rewardContainer.scale, { x: 0, y: 0 }, { x: GiftDiceRollsPage.REWARD_CONTAINER_SCALE, y: GiftDiceRollsPage.REWARD_CONTAINER_SCALE, duration: 0.7, ease: Bounce.easeOut }, "<");
        const backgroundScaleX = this.background.scale.x;
        const backgroundScaleY = this.background.scale.y;
        animationTimeLine.fromTo(this.background.scale, { x: 0.3, y: 0.3 }, { x: backgroundScaleX * 0.9, y: backgroundScaleY * 0.9, duration: 0.5, ease: Power2.easeOut }, "<");
        animationTimeLine.to(this.background.scale, { x: backgroundScaleX, y: backgroundScaleY, duration: 0.8, ease: "none" }, ">");
        this.fadeScreen.fadeIn();
        animationTimeLine.addPause("+=0.2");
        await animationTimeLine.play();
        animationTimeLine.kill();
        await this.startCollecting();
    }

    private async startCollecting(): Promise<void> {
        this.giftBoxPopupController.collectRollsHandle(this.diceRollAction.diceRollGiftRewardInDiceRolls);
        this.soundResources.assets_sounds_Coins_Top_Bar_mp3.play();
        await this.collectAnimation.startAnimation();
        this.rollProgress.visible = false;
        await this.hideView();
        this.viewCompletionSource.complete();
    }

    private async hideView(): Promise<void> {
        this.fadeScreen.fadeOut();
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.pause(0);
        animationTimeLine.to(this.scale, { x: 0, y: 0, duration: 0.25, ease: "power1.in" });
        animationTimeLine.to(this, { x: this.hideViewTarget.x, y: this.hideViewTarget.y, duration: 0.25, ease: "power1.in" }, "<");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
        if (this.collectAnimation.isActive) {
            this.collectAnimation.setAnimationProgress(1);
        }
    }

    protected override layout(width: number, height: number): void {
        this.background.width = width;
        this.background.height = height;
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.glow.width = width;
        this.glow.height = height;
        this.glow.position.set(width * 0.5, height * 0.5);
        this.background.position.set(width * 0.5, height * 0.5);
        this.rewardContainer.position.set(width * 0.5 - 10, height * 0.5 + this.rewardContainer.height * 0.05);
        this.wonLogo.position.set(width * 0.5 - 10, this.rewardContainer.y - this.rewardContainer.height * 0.72);
        this.collectAnimation.position.set(width * 0.5, height * 0.5);
        this.hideViewTarget = new Point(width * 0.45, height * 0.55);
        this.rollProgress.position.set(width * 0.5, 1680);
        this.collectAnimationPoint.set(this.rollProgress.position.x - this.collectAnimation.position.x, this.rollProgress.position.y - this.collectAnimation.position.y);
        super.layout(width, height);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        this.collectAnimation.diceCollected.remove(this.rollProgress.incrementProgress, this.rollProgress);
        super.destroy(options);
    }
}
